// ManagedSession — the runtime that turns a provider + a mic into a resilient
// stream. It owns capture, token minting, and the reconnect loop (F7/AC3); it
// does NOT own transcript state, so finalized text (held upstream in the store)
// survives a socket drop. Tokens are re-minted lazily on reconnect when expired
// (a live socket is authorized at handshake, so proactively re-minting mid-
// stream would interrupt for no gain).

import type { SpeechmaticsRegion } from '@/lib/keys/types'
import { createMicCapture, type MicCapture } from './audio/micCapture'
import type {
  ConnState,
  MintedToken,
  ProviderError,
  ProviderResult,
  TranscriptionProvider,
  TranscriptionSession
} from './types'

const MAX_RECONNECT = 4
const TOKEN_LEEWAY_MS = 60_000

/** Map a thrown start-time failure (getUserMedia, token mint) onto a
 *  ProviderError so the store can render it like any other (F9/AC3). */
function classifyStartError(e: unknown): ProviderError {
  const message = e instanceof Error ? e.message : String(e)
  const name = e instanceof Error ? e.name : ''
  // getUserMedia rejections come through as DOMExceptions.
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return { kind: 'auth', message: 'microphone permission denied', retryable: false }
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return { kind: 'format', message: 'no usable microphone found', retryable: false }
  }
  // Token mint failures embed the HTTP status in the message.
  if (/\b(401|403)\b|forbidden|unauthor/i.test(message)) {
    return { kind: 'auth', message, retryable: false }
  }
  if (/\b429\b|quota|rate limit/i.test(message)) {
    return { kind: 'quota', message, retryable: false }
  }
  return { kind: 'unknown', message, retryable: false }
}

export interface ManagedSessionCallbacks {
  onResult(result: ProviderResult): void
  onState(state: ConnState, detail?: string): void
  onError(err: ProviderError): void
  onLevel(level: number): void
}

export interface ManagedSessionOptions {
  provider: TranscriptionProvider
  apiKey: string
  deviceId?: string
  /** Pre-acquired tab/system audio (getDisplayMedia) to mix into capture. Owned
   *  here: kept alive across pause/resume, stopped on the final stop(). */
  tabStream?: MediaStream | null
  region?: SpeechmaticsRegion
  maxSpeakers?: number
  callbacks: ManagedSessionCallbacks
}

export interface ManagedSession {
  start(): Promise<void>
  pause(): Promise<void>
  resume(): Promise<void>
  stop(): Promise<void>
}

type Intent = 'idle' | 'running' | 'paused' | 'stopped'

export function createManagedSession(
  opts: ManagedSessionOptions
): ManagedSession {
  const { provider, apiKey, callbacks } = opts

  let intent: Intent = 'idle'
  let mic: MicCapture | null = null
  let session: TranscriptionSession | null = null
  let sampleRate = 16000
  let minted: MintedToken | null = null
  let suppressReconnect = false
  let reconnectAttempts = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null

  async function startCapture(): Promise<void> {
    mic = createMicCapture({ deviceId: opts.deviceId, tabStream: opts.tabStream })
    sampleRate = await mic.start(
      (pcm) => session?.sendAudio(pcm),
      (rms) => callbacks.onLevel(rms)
    )
  }

  async function openSocket(): Promise<void> {
    if (!minted || Date.now() > minted.expiresAt - TOKEN_LEEWAY_MS) {
      minted = await provider.mintToken(apiKey)
    }
    const s = provider.connect({
      token: minted.token,
      sampleRate,
      region: opts.region,
      maxSpeakers: opts.maxSpeakers
    })
    s.onResult(callbacks.onResult)
    s.onState(handleState)
    s.onError(handleError)
    session = s
  }

  function handleState(state: ConnState, detail?: string): void {
    if (state === 'live') {
      reconnectAttempts = 0
      callbacks.onState('live', detail)
      return
    }
    if (state === 'closed' || state === 'error') {
      if (suppressReconnect || intent !== 'running') {
        callbacks.onState(state, detail)
        return
      }
      scheduleReconnect()
      return
    }
    callbacks.onState(state, detail)
  }

  function handleError(err: ProviderError): void {
    callbacks.onError(err)
    // A non-retryable failure (bad key, bad format) must not loop forever.
    if (!err.retryable) suppressReconnect = true
  }

  function scheduleReconnect(): void {
    reconnectAttempts++
    if (reconnectAttempts > MAX_RECONNECT) {
      callbacks.onState('error', `reconnect failed after ${MAX_RECONNECT} tries`)
      return
    }
    callbacks.onState('reconnecting', `attempt ${reconnectAttempts}`)
    const delay = Math.min(8000, 500 * 2 ** (reconnectAttempts - 1))
    reconnectTimer = setTimeout(() => {
      if (intent !== 'running' || suppressReconnect) return
      openSocket().catch((e) => {
        handleError({
          kind: 'network',
          message: e instanceof Error ? e.message : String(e),
          retryable: true
        })
        if (!suppressReconnect) scheduleReconnect()
      })
    }, delay)
  }

  function clearReconnect(): void {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  async function teardownPipeline(): Promise<void> {
    clearReconnect()
    suppressReconnect = true
    const s = session
    session = null
    await s?.close().catch(() => {})
    await mic?.stop().catch(() => {})
    mic = null
  }

  async function open(): Promise<void> {
    intent = 'running'
    suppressReconnect = false
    reconnectAttempts = 0
    callbacks.onState('connecting', 'acquiring microphone')
    try {
      await startCapture()
      await openSocket()
    } catch (e) {
      // A failure before the socket is live (mic denied, token mint rejected)
      // never reaches handleError/handleState, so surface it here — otherwise
      // the chip is stuck on "connecting…" forever. Both start() and resume()
      // route through here, so both paths get the same treatment.
      await teardownPipeline()
      intent = 'idle'
      const err = classifyStartError(e)
      callbacks.onError(err)
      callbacks.onState('error', err.message)
    }
  }

  return {
    start: open,
    resume: open,

    async pause() {
      await teardownPipeline()
      intent = 'paused'
      callbacks.onState('paused')
    },

    async stop() {
      await teardownPipeline()
      // Final cut: release the caller-owned tab stream we kept alive across any
      // pause/resume. (teardownPipeline only stops the mic's own stream.)
      opts.tabStream?.getTracks().forEach((t) => t.stop())
      intent = 'stopped'
      callbacks.onState('closed', 'stopped')
    }
  }
}
